import { consoleLog } from '@Utils/consoleLog';
import { useClassStore } from '@Utils/useClassStore';
import { inject, injectable } from 'inversify';
import { action, observable } from 'mobx';

import UserAPI from '~gateways/user-api';
import container from '~ioc/ioc';
import AuthStore from '~stores/authentication';
import toastStore from '~stores/toast';
import type { Address, UserPrivacyConsentInfo } from '~types/gateways';
import type { Token, UserSettings } from '~types/models-types';

@injectable()
class AccountManagementStore {
  @inject(AuthStore)
  private AuthStore!: AuthStore;
  @observable error: string | null = null;
  @observable uiAccountManagementPanelOpen = false;
  @observable uiAddNewCreditCardPanelOpen = false;
  @observable uiAddNewAddressPanelOpen = false;
  @observable passwordCheck: string = '';
  @observable passwordCheckError: boolean = false;
  @observable password: string = '';
  @observable confirmPassword: string = '';
  @observable token: Token | null = null;
  @observable uiPasswordShown: boolean = false;
  @observable userSettings: UserSettings | null = null;
  @observable editAddressId: number | null = null;
  @observable addressDisplayName: string = '';
  @observable addressRecipientName: string = '';
  @observable addressLineOne: string = '';
  @observable addressTown: string = '';
  @observable addressCounty: string = '';
  @observable addressPostCode: string = '';
  @observable addressPhoneNumber: string = '';
  @observable addressToDeleteId: number | null = null;
  @observable cardToDeleteId: number | null = null;
  @observable addOrEditAddressMode: 'add' | 'edit' = 'add';
  @observable uiEditAddress: Address | null = null;

  @action public setAddOrEditAddressMode = (
    newState: 'add' | 'edit' = 'add'
  ) => {
    this.addOrEditAddressMode = newState;
  };

  @action public setCardToDeleteId = (cardId: number | null = null) =>
    (this.cardToDeleteId = cardId);

  @action public setAddressToDeleteId = (addressId: number | null = null) =>
    (this.addressToDeleteId = addressId);

  @action public setUiAddNewCreditCardPanelOpen = (open: boolean) =>
    (this.uiAddNewCreditCardPanelOpen = open);

  @action public saveNewPassword = async () => {
    if (!this.token) {
      return;
    }

    const response = await UserAPI.updateUserPassword(
      this.token.token,
      this.password,
      this.confirmPassword
    );
    if (response?.success) {
      this.error = null;
      toastStore.addMessage({
        message: 'Password Successfully Updated',
      });
      return;
    }
    this.error = 'Error attempting to change password, please try again';
  };

  @action public updatePassword = async (
    password: string,
    confirmPassword: string,
    token: string
  ) => UserAPI.updateUserPassword(token, password, confirmPassword);

  @action public getToken = async (password: string) => {
    const token = await UserAPI.getUserSettingsToken(password);
    if (token?.success) {
      this.token = token;
      this.uiPasswordShown = false;
      await this.getUserSettings();
      this.passwordCheck = '';
      return;
    }
    this.passwordCheckError = true;
  };

  @action public isExpired = () => {
    if (this.token) {
      const date = new Date();
      date.setDate(date.getDate());
      const expires = new Date(this.token.expires);
      if (this.token && expires < date) {
        return true;
      }
      if (this.token && expires > date) {
        return false;
      }
    }
  };

  @action public setUserDetails = async (name: string) => {
    if (this.token?.token) {
      const response = await UserAPI.setUserDetails(this.token?.token, name);
      if (response?.success) {
        this.error = null;
        this.uiAccountManagementPanelOpen = false;
        toastStore.addMessage({
          message: 'Details Successfully Updated',
        });
        return;
      }
      this.error = 'Error attempting to update details, please try again';
    }
  };

  @action public setUserPrivacyConsent = async ({
    enable,
    onBeforeRequest,
    onRequestComplete,
    onRequestError,
  }: Omit<UserPrivacyConsentInfo, 'token'>) => {
    if (this.token?.token) {
      await UserAPI.setUserPrivacyConsent({
        enable,
        token: this.token.token,
        onBeforeRequest,
        onRequestComplete,
        onRequestError,
      });
    }
  };

  @action public getUserSettings = async () => {
    if (this.token?.token) {
      this.userSettings = await UserAPI.getUserSettings(this.token.token);
    } else if (__DEV__) {
      consoleLog(
        'No token in AccountManagementStore when calling AccountManagementStore.getUserSettings()'
      );
    }
  };

  @action public addUserAddress = async () => {
    if (this.token?.token) {
      const addressToAdd = {
        token: this.token.token,
        displayName: this.addressDisplayName,
        isDefault: true,
        recipientName: this.addressRecipientName,
        street: this.addressLineOne,
        townOrCity: this.addressTown,
        county: this.addressCounty,
        postCode: this.addressPostCode,
        phone: this.addressPhoneNumber,
      };
      const response = await UserAPI.addUserAddress(addressToAdd);
      this.userSettings = {
        ...this.userSettings,
        savedAddresses: response.savedAddresses,
      } as UserSettings;
    }
  };

  @action public saveEditedUserAddress = async () => {
    if (this.token?.token) {
      const editedAddress = {
        token: this.token.token,
        displayName: this.addressDisplayName,
        recipientName: this.addressRecipientName,
        street: this.addressLineOne,
        townOrCity: this.addressTown,
        county: this.addressCounty,
        postCode: this.addressPostCode,
        phone: this.addressPhoneNumber,
        savedAddressId: this.editAddressId,
      };
      const response = await UserAPI.editUserAddress(editedAddress);

      // Only update user settings if API call was successful
      // Without this, the saved addresses will be blank if the call fails
      if (response.success) {
        this.userSettings = {
          ...this.userSettings,
          savedAddresses: response.savedAddresses,
        } as UserSettings;
      }
    }
  };

  @action public setDefaultAddress = async (addressId: number) => {
    if (this.token?.token) {
      const response = await UserAPI.setDefaultSavedAddress(
        this.token.token,
        addressId
      );
      this.userSettings = {
        ...this.userSettings,
        savedAddresses: response.savedAddresses,
      } as UserSettings;
    }
  };

  @action public setDefaultCard = async (cardId: number) => {
    if (this.token?.token) {
      const response = await UserAPI.setDefaultCard(cardId, this.token.token);
      this.userSettings = {
        ...this.userSettings,
        savedCards: response.savedCards,
      } as UserSettings;
    }
  };

  @action public deleteAddress = async (addressId: number) => {
    if (this.token?.token) {
      const response = await UserAPI.removeSavedAddress(
        this.token.token,
        addressId
      );
      this.userSettings = {
        ...this.userSettings,
        savedAddresses: response.savedAddresses,
      } as UserSettings;
    }
    this.addressToDeleteId = null;
  };

  @action public addCard = async (
    displayName: string,
    isDefault: boolean,
    paymentMethodId: string
  ) => {
    if (this.token?.token) {
      const response = await UserAPI.addSavedCard({
        token: this.token.token,
        displayName,
        isDefault,
        id: paymentMethodId,
      });

      if (this.userSettings && response?.success) {
        this.userSettings = {
          ...this.userSettings,
          savedCards: response.savedCards,
        };
      }

      return response;
    }
  };

  @action public removeCard = async (id: number) => {
    if (this.token?.token) {
      const response = await UserAPI.removeSavedCard(this.token?.token, id);

      if (this.userSettings && response?.success) {
        this.userSettings = {
          ...this.userSettings,
          savedCards: response.savedCards,
        };
      }

      return response;
    }
  };
}

export const useAccountManagementStore = () =>
  useClassStore<AccountManagementStore>(container.get(AccountManagementStore));

export default AccountManagementStore;
